import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

// Lazy load heavy components
const UserList = React.lazy(() => import('./components/users/UserList'));
const PartnerList = React.lazy(() => import('./components/partners/PartnerList'));
const ProjectList = React.lazy(() => import('./components/projects/ProjectList'));
const ProjectDetail = React.lazy(() => import('./components/projects/ProjectDetail'));
const ProjectCreateEdit = React.lazy(() => import('./components/projects/ProjectCreateEdit'));
const PartnerDashboard = React.lazy(() => import('./components/partners/PartnerDashboard'));
const PartnerProjects = React.lazy(() => import('./components/partners/PartnerProjects'));
const PartnerReports = React.lazy(() => import('./components/partners/PartnerReports'));
const PartnerMonitoring = React.lazy(() => import('./components/partners/PartnerMonitoring'));
const SampleMonitoringPage = React.lazy(() => import('./components/samples/SampleMonitoringPage'));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mangrove-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
          <Routes>
            <Route path="/login" element={<Login />} />
          
          {/* Internal User Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute requiredUserType="internal">
              <Layout><Dashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute requiredUserType="internal">
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <UserList />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/partners" element={
            <ProtectedRoute requiredUserType="internal">
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <PartnerList />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/projects" element={
            <ProtectedRoute requiredUserType="internal">
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <ProjectList />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/projects/create" element={
            <ProtectedRoute requiredUserType="internal">
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <ProjectCreateEdit />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/projects/:id/edit" element={
            <ProtectedRoute requiredUserType="internal">
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <ProjectCreateEdit />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Partner Routes */}
          <Route path="/partner/dashboard" element={
            <ProtectedRoute requiredUserType="partner">
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <PartnerDashboard />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/partner/projects" element={
            <ProtectedRoute requiredUserType="partner">
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <PartnerProjects />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/partner/reports" element={
            <ProtectedRoute requiredUserType="partner">
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <PartnerReports />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/partner/monitoring" element={
            <ProtectedRoute requiredUserType="partner">
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <PartnerMonitoring />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Shared Routes - accessible by both internal and partner users */}
          <Route path="/projects/:id" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <ProjectDetail />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/projects/:projectId/samples/:sampleId/monitoring" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <SampleMonitoringPage />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Default Route */}
          <Route path="/" element={<Login />} />
          </Routes>
        </div>
      </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;